import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { cldThumb } from '../utils/img';
import { useAuth } from '../context/AuthContext';
import { getPackableAppointments, getInventoryForAppointment, suggestPack } from '../api/dispatch';
import { getSizes, getCategories, getUseCases } from '../api/meta';

// ─────────────────────────────────────────────────────────────────────────────
// SUPPLY SANDBOX (/dummy). Blue-themed clone of the packing flow. It reads live
// stock READ-ONLY and simulates packing entirely in the browser — nothing is
// ever written to the database. "Packed" pieces drop out of the available pool
// (client-side `consumed` set) so supply gaps surface as stock depletes.
// State is in-memory: it resets on reload (or via the Reset button).
// ─────────────────────────────────────────────────────────────────────────────

const GENDERS = ['girl', 'boy', 'unisex'];
function genderShort(g) {
  const k = (g || '').toLowerCase();
  return k === 'boy' ? 'B' : k === 'girl' ? 'G' : k === 'unisex' ? 'U' : (g || '');
}
function matchUseCases(tagStr, all) {
  if (!tagStr) return [];
  const norm = String(tagStr).toLowerCase().replace(/-/g, '');
  return all.filter(u => norm.includes(u.toLowerCase().replace(/-/g, '')));
}

// ── Packing modal (simulated) ────────────────────────────────────────────────
function PackModal({ appt, consumed, allSizes, allCategories, allUseCases, onPack, onClose }) {
  const children = appt.children || [];
  const [childIdx, setChildIdx] = useState(0);
  const child = children[childIdx] || {};

  const [sizes, setSizes] = useState([]);
  const [genders, setGenders] = useState([]);
  const [selCats, setSelCats] = useState([]);
  const [selUcs, setSelUcs] = useState([]);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [selMeta, setSelMeta] = useState({});
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState(null);

  useEffect(() => { setSelCats(allCategories); }, [allCategories]);
  useEffect(() => { setSelUcs(allUseCases); }, [allUseCases]);
  useEffect(() => {
    setSizes(child.size ? [child.size] : []);
    setGenders(child.gender ? [child.gender, 'unisex'] : ['unisex']);
    setSuggestError(null);
  }, [childIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (sizes.length === 0 || genders.length === 0) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try { setItems(await getInventoryForAppointment(appt.appointment_id, { sizes, genders })); }
    catch (e) { setItems([]); }
    finally { setLoading(false); }
  }, [appt.appointment_id, sizes, genders]);
  useEffect(() => { load(); }, [load]);

  const catSet = useMemo(() => new Set(selCats), [selCats]);
  const ucSet = useMemo(() => new Set(selUcs), [selUcs]);
  const allCatsSel = allCategories.length > 0 && allCategories.every(c => catSet.has(c));
  const allUcsSel = allUseCases.length > 0 && allUseCases.every(u => ucSet.has(u));
  const s = search.trim().toLowerCase();

  // Available = live items minus already-consumed (simulated) minus filters.
  const visible = useMemo(() => items.filter(it => {
    if (consumed.has(it.item_id)) return false;
    if (it.category) { if (!catSet.has(it.category)) return false; } else if (!allCatsSel) return false;
    const ucs = matchUseCases(it.use_case_tags, allUseCases);
    if (ucs.length) { if (!ucs.some(u => ucSet.has(u))) return false; } else if (!allUcsSel) return false;
    if (s && !((it.zoddle_barcode || '').toLowerCase().includes(s) ||
               (it.design_name || '').toLowerCase().includes(s) ||
               (it.brand_name || '').toLowerCase().includes(s))) return false;
    return true;
  }), [items, consumed, catSet, ucSet, allCatsSel, allUcsSel, allUseCases, s]);

  function toggle(it) {
    setSelected(prev => { const n = new Set(prev); n.has(it.item_id) ? n.delete(it.item_id) : n.add(it.item_id); return n; });
    setSelMeta(prev => ({ ...prev, [it.item_id]: it }));
  }

  // Stage 1 (AUTO_PACK_BUILD_CONTRACT.md): pre-fills the SAME basket state
  // the manual click flow writes to, exactly as if each suggested item had
  // been clicked by hand. Suggested items already sandbox-"consumed" this
  // session are dropped — the backend only knows real DB state, not the
  // sandbox's client-side simulation, so this filter is the sandbox's own
  // responsibility, same as the visible grid's own `consumed` filter above.
  async function handleSuggest() {
    if (!child.appointment_child_id) return;
    setSuggesting(true);
    setSuggestError(null);
    try {
      const result = await suggestPack(appt.appointment_id, child.appointment_child_id);
      if (result.error) { setSuggestError(result.error); return; }
      const fresh = (result.items || []).filter(it => !consumed.has(it.item_id));
      setSelected(new Set(fresh.map(it => it.item_id)));
      setSelMeta(prev => {
        const next = { ...prev };
        fresh.forEach(it => { next[it.item_id] = it; });
        return next;
      });
    } catch (e) {
      setSuggestError(e?.response?.data?.detail || 'Suggest Pack failed — see console.');
      console.error(e);
    } finally {
      setSuggesting(false);
    }
  }
  const basket = useMemo(() => Array.from(selected).map(id => selMeta[id]).filter(Boolean), [selected, selMeta]);
  const chip = (val, on, onClick) => (
    <button key={val} onClick={onClick}
      className={`text-xs font-semibold py-1 px-2.5 rounded-full border capitalize ${on ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-500'}`}>{val}</button>
  );
  const toggleIn = (arr, set, val) => set(arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-3" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-[92vw] h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-3 border-b border-gray-100">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-gray-800">{appt.customer_name || '—'}
                <span className="ml-2 text-xs text-gray-400 font-normal">{appt.scheduled_date} {appt.scheduled_time} · UID <span className="font-mono">{appt.zcp_appointment_uid || '—'}</span></span></h2>
              {children.length > 1 && (
                <div className="flex gap-2 mt-1">
                  {children.map((c, i) => (
                    <button key={i} onClick={() => setChildIdx(i)}
                      className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${i === childIdx ? 'bg-zoddle-green text-white border-zoddle-green' : 'border-gray-200 text-gray-500'}`}>{c.size} {genderShort(c.gender)}</button>
                  ))}
                </div>
              )}
            </div>
            {/* customer's requirements for the active child */}
            <div className="flex items-start gap-3">
              <div className="bg-zoddle-blue/5 border border-zoddle-blue/20 rounded-lg px-3 py-2 max-w-sm">
                <p className="text-xxs uppercase font-semibold text-zoddle-blue mb-0.5">Requirements · {child.size} {genderShort(child.gender)}</p>
                {(() => {
                  const prefs = [
                    ['Brand', child.brand_pref], ['Use', child.use_case_pref],
                    ['Style', child.style_pref], ['Avoid', child.avoid_tags],
                  ].filter(([, v]) => v);
                  return prefs.length === 0
                    ? <p className="text-xs text-gray-400 italic">No specific requirements</p>
                    : <div className="text-xs text-gray-700 space-y-0.5">
                        {prefs.map(([label, val]) => (
                          <p key={label}><span className="text-gray-400">{label}: </span>{val}</p>
                        ))}
                      </div>;
                })()}
              </div>
              <button onClick={handleSuggest} disabled={suggesting || !child.appointment_child_id}
                className="bg-zoddle-blue text-white text-sm font-bold px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 shrink-0">
                {suggesting ? 'Suggesting…' : '✨ Suggest Pack'}
              </button>
              <button onClick={onClose} className="text-sm text-gray-400 hover:text-gray-600 shrink-0">✕ Close</button>
            </div>
          </div>
          {suggestError && <p className="text-xs text-zoddle-pink mt-1.5">Suggest Pack: {suggestError}</p>}
          {/* filters */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2">
            <div className="flex items-center gap-1"><span className="text-xxs uppercase text-gray-400 mr-1">Gender</span>{GENDERS.map(g => chip(g, genders.includes(g), () => toggleIn(genders, setGenders, g)))}</div>
            <div className="flex items-center gap-1 flex-wrap"><span className="text-xxs uppercase text-gray-400 mr-1">Size</span>{allSizes.map(sz => chip(sz, sizes.includes(sz), () => toggleIn(sizes, setSizes, sz)))}</div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-1.5">
            <div className="flex items-center gap-1 flex-wrap"><span className="text-xxs uppercase text-gray-400 mr-1">Category</span>{allCategories.map(c => chip(c, catSet.has(c), () => toggleIn(selCats, setSelCats, c)))}</div>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search barcode / design / brand"
              className="ml-auto border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-zoddle-green" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? <div className="text-center py-20 text-gray-400 text-sm">Loading available stock…</div>
          : visible.length === 0 ? <div className="text-center py-20 text-gray-400 text-sm">No available pieces for this filter (stock may be depleted).</div>
          : (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {visible.map(it => {
                const on = selected.has(it.item_id);
                return (
                  <div key={it.item_id} onClick={() => toggle(it)}
                    className={`border-2 rounded-xl p-2 cursor-pointer transition ${on ? 'border-zoddle-green bg-zoddle-green/10' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                    {it.image_url_1
                      ? <img src={cldThumb(it.image_url_1, 300)} alt="" className="w-full aspect-square object-cover rounded-lg mb-2 bg-gray-100" loading="lazy" />
                      : <div className="w-full aspect-square bg-gray-100 rounded-lg mb-2 flex items-center justify-center text-gray-300 text-xs">No image</div>}
                    <p className="text-xs font-semibold text-gray-800 truncate">{it.brand_name || '—'}</p>
                    <p className="text-xs text-gray-500 truncate">{it.size} · {genderShort(it.gender)} · <span className="font-bold text-zoddle-green">₹{it.zoddle_price}</span></p>
                    <p className="text-xs font-mono text-gray-400 truncate">{it.zoddle_barcode}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 flex items-center gap-3">
          <span className="text-xs text-gray-500">{visible.length} available · <b className="text-gray-800">{selected.size}</b> selected</span>
          <div className="flex-1 flex flex-wrap gap-1 max-h-14 overflow-y-auto">
            {basket.map(it => (
              <span key={it.item_id} className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2 py-0.5 text-xs">
                <span className="font-mono text-gray-600">{it.zoddle_barcode}</span>
                <button onClick={() => toggle(it)} className="text-zoddle-pink font-bold">×</button>
              </span>
            ))}
          </div>
          <button onClick={() => onPack(appt, basket)} disabled={selected.size === 0}
            className="bg-zoddle-green text-white text-sm font-bold px-6 py-2.5 rounded-lg hover:opacity-90 disabled:opacity-50 transition">
            Pack {selected.size} (simulate)
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main sandbox app ──────────────────────────────────────────────────────────
export default function DummyPackApp() {
  const { auth, logout } = useAuth();
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allSizes, setAllSizes] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allUseCases, setAllUseCases] = useState([]);
  const [active, setActive] = useState(null);
  const [consumed, setConsumed] = useState(new Set());   // item_ids simulated-packed
  const [packs, setPacks] = useState([]);                // {uid, customer, count, at}
  const [packedAppts, setPackedAppts] = useState({});    // appointment_id -> times packed
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setAppts(await getPackableAppointments()); } catch (e) { setAppts([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getSizes().then(setAllSizes).catch(() => {});
    getCategories().then(setAllCategories).catch(() => {});
    getUseCases().then(setAllUseCases).catch(() => {});
  }, []);

  function handlePack(appt, basket) {
    setConsumed(prev => { const n = new Set(prev); basket.forEach(it => n.add(it.item_id)); return n; });
    setPacks(prev => [{ uid: appt.zcp_appointment_uid, customer: appt.customer_name, count: basket.length,
                        at: new Date().toLocaleTimeString() }, ...prev]);
    setPackedAppts(prev => ({ ...prev, [appt.appointment_id]: (prev[appt.appointment_id] || 0) + 1 }));
    setActive(null);
    setToast(`Simulated pack: ${basket.length} pieces for ${appt.customer_name || 'order'} — now unavailable`);
    setTimeout(() => setToast(null), 3500);
  }
  function reset() {
    if (!window.confirm('Reset the sandbox? All simulated packs clear and full stock returns.')) return;
    setConsumed(new Set()); setPacks([]); setPackedAppts({});
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <h1 className="text-zoddle-green font-black text-xl">ZOCS</h1>
          <span className="bg-zoddle-blue/15 text-zoddle-blue text-xs font-bold px-2.5 py-0.5 rounded-full">SUPPLY SANDBOX</span>
          <span className="text-xs text-gray-400 hidden sm:inline">simulation only · nothing is saved</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm"><b className="text-gray-800">{packs.length}</b> <span className="text-gray-500">packs</span> · <b className="text-gray-800">{consumed.size}</b> <span className="text-gray-500">pieces consumed</span></span>
          <button onClick={reset} className="text-sm font-semibold text-zoddle-pink hover:opacity-80">Reset</button>
          <span className="text-gray-700 text-sm font-medium">{auth.full_name}</span>
          <button onClick={logout} className="text-sm text-gray-400 hover:text-zoddle-pink">Sign out</button>
        </div>
      </nav>

      {toast && <div className="bg-zoddle-blue/10 border-b border-zoddle-blue/30 text-zoddle-blue text-sm px-6 py-2">{toast}</div>}

      <main className="max-w-screen-xl mx-auto p-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div>
          <h2 className="text-lg font-bold text-gray-800 mb-1">Appointments to pack</h2>
          <p className="text-sm text-gray-400 mb-4">Pack pieces to see stock deplete. Packed pieces become unavailable across all appointments.</p>
          {loading ? <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
          : appts.length === 0 ? <div className="text-center py-16 text-gray-400 text-sm">No packable appointments right now.</div>
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {appts.map(a => (
                <div key={a.appointment_id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800">{a.customer_name || '—'}
                      {packedAppts[a.appointment_id] && <span className="ml-2 text-xxs bg-zoddle-green/15 text-zoddle-green font-bold px-1.5 py-0.5 rounded">packed ×{packedAppts[a.appointment_id]}</span>}</p>
                    <p className="text-xs text-gray-400">{a.scheduled_date} {a.scheduled_time || ''} · UID <span className="font-mono">{a.zcp_appointment_uid || '—'}</span></p>
                    <p className="text-xs text-gray-500 mt-0.5">{(a.children || []).map(c => `${c.size} ${genderShort(c.gender)}`).join(', ') || '—'}</p>
                  </div>
                  <button onClick={() => setActive(a)}
                    className="bg-zoddle-green text-white text-sm font-bold px-5 py-2 rounded-lg hover:opacity-90 shrink-0">Pack</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <aside>
          <h2 className="text-lg font-bold text-gray-800 mb-1">Simulated packs</h2>
          <p className="text-sm text-gray-400 mb-4">{consumed.size} pieces removed from stock this session.</p>
          {packs.length === 0 ? <div className="text-center py-10 text-gray-300 text-sm border border-dashed border-gray-200 rounded-xl">Nothing packed yet</div>
          : (
            <div className="space-y-2">
              {packs.map((p, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm">
                  <p className="font-semibold text-gray-800">{p.customer || '—'} <span className="text-xs font-normal text-gray-400">· {p.uid || '—'}</span></p>
                  <p className="text-xs text-gray-500">{p.count} pieces · {p.at}</p>
                </div>
              ))}
            </div>
          )}
        </aside>
      </main>

      {active && (
        <PackModal appt={active} consumed={consumed} allSizes={allSizes}
          allCategories={allCategories} allUseCases={allUseCases}
          onPack={handlePack} onClose={() => setActive(null)} />
      )}
    </div>
  );
}
